import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { FormData, FormErrors } from '../types';
import { Hero } from '../components/Hero';
import { ProgressBar } from '../components/ProgressBar';
import { SuccessScreen } from '../components/SuccessScreen';
// Import all sections
import { Section1 } from '../components/sections/Section1';
import { Section2 } from '../components/sections/Section2';
import { Section3 } from '../components/sections/Section3';
import { Section4 } from '../components/sections/Section4';
import { Section5 } from '../components/sections/Section5';
import { Section6 } from '../components/sections/Section6';
import { Section7 } from '../components/sections/Section7';
import { Section8 } from '../components/sections/Section8';
import { Section9 } from '../components/sections/Section9';
const TOTAL_SECTIONS = 9;
const initialData: FormData = {
  nombre: '',
  edad: '',
  email: '',
  telefono: '',
  ciudad: '',
  objetivo: '',
  importancia: '',
  sentimiento: '',
  pesoActual: '',
  pesoDeseado: '',
  energia: 5,
  sueno: '',
  condiciones: '',
  comidas: '',
  agua: '',
  procesada: '',
  alimentos: '',
  restricciones: '',
  ejercicio: '',
  actividad: [],
  tiempo: '',
  estres: 5,
  manejoEstres: '',
  meditacion: '',
  pensamientos: '',
  conexion: '',
  practicas: '',
  significado: '',
  obstaculos: '',
  acompanamiento: '',
  disponibilidad: '',
  inicio: '',
  presupuesto: '',
  comentarios: '',
  consentimiento: false
};
export function Diagnostico() {
  const [currentSection, setCurrentSection] = useState(1);
  const [data, setData] = useState<FormData>(initialData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  // Scroll to top when section changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [currentSection, isSubmitted]);
  const handleChange = (field: keyof FormData, value: any) => {
    setData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[field];
        return newErrors;
      });
    }
  };
  const validateSection = (section: number): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;
    const requireField = (field: keyof FormData, msg: string = 'Este campo es requerido') => {
      if (!data[field] || Array.isArray(data[field]) && (data[field] as any[]).length === 0) {
        newErrors[field] = msg;
        isValid = false;
      }
    };
    switch (section) {
      case 1:
        requireField('nombre');
        requireField('edad');
        requireField('email');
        requireField('telefono');
        requireField('ciudad');
        if (data.email && !/^\S+@\S+\.\S+$/.test(data.email)) {
          newErrors.email = 'Ingresa un correo válido';
          isValid = false;
        }
        break;
      case 2:
        requireField('objetivo');
        requireField('importancia');
        requireField('sentimiento');
        break;
      case 3:
        requireField('pesoActual');
        requireField('pesoDeseado');
        requireField('sueno');
        break;
      case 4:
        requireField('comidas');
        requireField('agua');
        requireField('procesada');
        requireField('alimentos');
        break;
      case 5:
        requireField('ejercicio');
        requireField('actividad', 'Selecciona al menos una actividad');
        requireField('tiempo');
        break;
      case 6:
        requireField('manejoEstres');
        requireField('meditacion');
        requireField('pensamientos');
        break;
      case 7:
        requireField('conexion');
        requireField('significado');
        break;
      case 8:
        requireField('obstaculos');
        requireField('acompanamiento');
        requireField('disponibilidad');
        break;
      case 9:
        requireField('inicio');
        requireField('presupuesto');
        if (!data.consentimiento) {
          newErrors.consentimiento = 'Debes aceptar los términos para continuar';
          isValid = false;
        }
        break;
    }
    setErrors(newErrors);
    return isValid;
  };
  const handleNext = () => {
    if (validateSection(currentSection)) {
      setCurrentSection(prev => Math.min(prev + 1, TOTAL_SECTIONS));
    } else {
      // Small delay to allow state update, then scroll to first error
      setTimeout(() => {
        const firstError = document.querySelector('.text-red-600');
        if (firstError) {
          firstError.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 100);
    }
  };
  const handlePrev = () => {
    setCurrentSection(prev => Math.max(prev - 1, 1));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateSection(9)) {
      // Here you would typically send the data to an API
      console.log('Form submitted:', data);
      setIsSubmitted(true);
    }
  };
  const renderSection = () => {
    const props = {
      data,
      errors,
      onChange: handleChange
    };
    switch (currentSection) {
      case 1:
        return <Section1 {...props} data-id="element-213" />;
      case 2:
        return <Section2 {...props} data-id="element-214" />;
      case 3:
        return <Section3 {...props} data-id="element-215" />;
      case 4:
        return <Section4 {...props} data-id="element-216" />;
      case 5:
        return <Section5 {...props} data-id="element-217" />;
      case 6:
        return <Section6 {...props} data-id="element-218" />;
      case 7:
        return <Section7 {...props} data-id="element-219" />;
      case 8:
        return <Section8 {...props} data-id="element-220" />;
      case 9:
        return <Section9 {...props} data-id="element-221" />;
      default:
        return null;
    }
  };
  if (isSubmitted) {
    return <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center" data-id="element-222">
        <SuccessScreen name={data.nombre} objective={data.objetivo} onReset={() => {
        setData(initialData);
        setCurrentSection(1);
        setIsSubmitted(false);
      }} data-id="element-223" />
      </div>;
  }
  return <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-id="element-224">
      <div className="max-w-3xl mx-auto" data-id="element-225">
        <Hero data-id="element-226" />

        <div className="bg-blanco-perla rounded-2xl p-6 md:p-8 mb-6 shadow-premium" data-id="element-227">
          <p className="text-lg text-azul-profundo font-medium mb-4" data-id="element-228">
            Este diagnóstico nos ayudará a conocer tu momento actual, tus
            objetivos, tus hábitos, tus retos y el nivel de acompañamiento que
            necesitas.
          </p>
          <p className="text-gray-600 mb-4" data-id="element-229">
            Responde con sinceridad y detalle. Con esta información podremos
            orientarte hacia un plan inicial más personalizado para ayudarte a
            crear resultados sostenibles a corto, mediano y largo plazo.
          </p>
          <p className="text-gray-600" data-id="element-230">
            Este programa no se basa en soluciones rápidas, sino en estructura,
            seguimiento, alimentación saludable, movimiento, mentalidad y
            acompañamiento durante 90 días.
          </p>
        </div>

        <div className="border border-dorado/30 rounded-xl p-4 mb-8 text-sm font-[600] text-[#C9A96E] bg-[#0D1B3E]" data-id="element-231">
          <strong className="font-semibold" data-id="element-232">Nota importante:</strong> Este
          diagnóstico no sustituye una consulta médica. Consulta con tu
          profesional de salud antes de iniciar cualquier plan.
        </div>

        <ProgressBar currentSection={currentSection} totalSections={TOTAL_SECTIONS} data-id="element-233" />

        <form onSubmit={handleSubmit} className="bg-blanco-perla rounded-2xl p-6 md:p-8 shadow-premium relative overflow-hidden" data-id="element-234">
          <AnimatePresence mode="wait" data-id="element-235">
            <motion.div key={currentSection} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-236">
              {renderSection()}
            </motion.div>
          </AnimatePresence>

          <div className="mt-10 pt-6 border-t border-plateado/30 flex flex-col-reverse sm:flex-row justify-between gap-4" data-id="element-237">
            {currentSection > 1 ? <button type="button" onClick={handlePrev} className="flex items-center justify-center px-6 py-3 border-2 border-azul-profundo text-azul-profundo rounded-full font-medium hover:bg-azul-profundo hover:text-blanco-perla transition-colors w-full sm:w-auto" data-id="element-238">
                  <ArrowLeft className="w-5 h-5 mr-2" data-id="element-239" />
                  Volver
                </button> : <div data-id="element-240"></div> // Spacer
          }

            {currentSection < TOTAL_SECTIONS ? <button type="button" onClick={handleNext} className="flex items-center justify-center px-8 py-3 bg-azul-profundo text-blanco-perla rounded-full font-medium hover:bg-azul-medio transition-all shadow-md hover:shadow-lg w-full sm:w-auto" data-id="element-241">
                Continuar
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-242" />
              </button> : <button type="submit" className="flex items-center justify-center px-8 py-4 bg-dorado text-azul-profundo rounded-full font-bold text-lg hover:bg-[#b8985d] transition-all shadow-md hover:shadow-lg w-full sm:w-auto" data-id="element-243">
                Enviar mi Diagnóstico
              </button>}
          </div>
        </form>
      </div>
    </div>;
}